// Copyright 2024, Durable
/*
 * An array of lazy imports for pages available to the users of durable.
 */

import React, { ReactElement } from "react";
import {
  Add as AddIcon,
  Article as ArticleIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Work as WorkIcon
} from "@mui/icons-material";

const NewJobPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "start" */
      /* webpackMode: "lazy" */
      "../pages/NewJob"
    )
);
const Blueprint = React.lazy(
  () =>
    import(
      /* webpackChunkName: "blueprint" */
      /* webpackMode: "lazy" */
      "../pages/Blueprint"
    )
);
const BlueprintList = React.lazy(
  () =>
    import(
      /* webpackChunkName: "blueprintlist" */
      /* webpackMode: "lazy" */
      "../pages/BlueprintList"
    )
);
const SettingsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "settings" */
      /* webpackMode: "lazy" */
      "../pages/Settings"
    )
);

export type route = {
  // A unique ID for the route
  id: string;
  // A given title for the route, will be the text for navigation
  name: string;
  // The URL location which should load this route
  route: string;
  // The lazy-loaded page element that should be displayed at the route.
  element: () => ReactElement;
  // A short description of the purpose of this route.
  desc: string;
  // The relevant icon for the route. User routes should be solid, dev outlined
  icon: () => ReactElement;
  // OPTIONAL: Dont populate navigation links for this route
  routeOnly?: boolean;
  // OPTIONAL: Create navigation links for this route, but dont route to it
  dontRoute?: boolean;
  // OPTIONAL: Relevant tags describing this route.
  tags?: Array<string>;
};

// An array containing all current pages a user may access on durable.
// Populating this array will automatically route your debug tool and populate
// all navigation.
const userPages: Array<route> = [
  {
    id: "start",
    name: "New Workflow",
    route: "/app/start",
    element() {
      return <NewJobPage />;
    },
    desc: "Begin the creation of a new workflow blueprint.",
    icon() {
      return <AddIcon fontSize="inherit" />;
    }
  },
  {
    id: "blueprint",
    name: "Blueprint",
    route: "/app/blueprint",
    element() {
      return <Blueprint />;
    },
    desc: "Specify the details about a app by modifying a blueprint.",
    icon() {
      return <ArticleIcon fontSize="inherit" />;
    },
    routeOnly: true
  },
  {
    id: "workflow_list",
    name: "Your Workflows",
    route: "/app/workflows",
    element() {
      return <BlueprintList />;
    },
    desc: "A list of all the blueprints you have created.",
    icon() {
      return <WorkIcon fontSize="inherit" />;
    }
  },
  {
    id: "settings",
    name: "Settings",
    route: "/app/settings",
    element() {
      return <SettingsPage />;
    },
    desc: "Configure and customize how durable works.",
    icon() {
      return <SettingsIcon fontSize="inherit" />;
    }
  },
  {
    id: "help_center",
    name: "Help Center",
    route: "https://docs.durable.ai",
    element() {
      return <></>; // Placeholder element as we are redirecting to a subdomain.
    },
    desc: "Configure and customize how durable works.",
    icon() {
      return <HelpIcon fontSize="inherit" />;
    },
    routeOnly: false
  }
];

export default userPages;
